import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { motion } from 'framer-motion';

const CustomerConversionBlock = () => {
  const [postcode, setPostcode] = useState('');
  const [woningType, setWoningType] = useState('');

  const benefits = [
    "Persoonlijk advies op maat voor uw woning",
    "Vergelijk meerdere aanbieders zonder verplichtingen",
    "Onafhankelijke informatie over kosten en subsidies"
  ];

  return (
    <motion.section 
      className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-primary/10 to-secondary/10 relative overflow-hidden"
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      {/* Animated background elements */}
      <motion.div
        className="absolute top-10 left-10 w-20 h-20 bg-primary/20 rounded-full blur-xl"
        animate={{
          scale: [1, 1.5, 1],
          x: [0, 30, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-10 right-10 w-32 h-32 bg-secondary/20 rounded-full blur-xl"
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -20, 0],
          y: [0, 20, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="bg-card border border-border rounded-2xl shadow-[0_15px_40px_-8px_rgba(0,0,0,0.2)] hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden transform perspective-1000"
          whileHover={{ scale: 1.02, rotateY: 2, boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.3), 0 0 30px rgba(59, 130, 246, 0.1)" }}
          transition={{ duration: 0.3 }}
          style={{ transformStyle: 'preserve-3d' }}
        >
          <div className="p-8 lg:p-12" style={{ transform: 'translateZ(20px)' }}>
            {/* Header */}
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <motion.div 
                className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 shadow-[0_8px_20px_-4px_rgba(0,0,0,0.15)]"
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                }}
                whileHover={{ 
                  scale: 1.2,
                  boxShadow: "0 12px 30px -5px rgba(0, 0, 0, 0.25)"
                }}
                style={{ transform: 'translateZ(30px)', transformStyle: 'preserve-3d' }}
              >
                <Icon name="Home" size={32} className="text-primary" />
              </motion.div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
                Wilt u weten wat voor úw woning slim is?
              </h2>
              <p className="text-lg font-body text-muted-foreground">
                Start met gratis persoonlijk advies en ontdek uw mogelijkheden
              </p>
            </motion.div>

            {/* Benefits */}
            <motion.div 
              className="space-y-3 mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ staggerChildren: 0.1 }}
            >
              {benefits?.map((benefit, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-start space-x-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 5 }}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <motion.div 
                      className="w-5 h-5 bg-success rounded-full flex items-center justify-center shadow-[0_3px_10px_rgba(0,0,0,0.12)]"
                      whileHover={{ scale: 1.3, rotate: 360, boxShadow: "0 6px 16px rgba(0,0,0,0.2)" }}
                      transition={{ duration: 0.4 }}
                      style={{ transform: 'translateZ(15px)' }}
                    >
                      <Icon name="Check" size={14} color="white" />
                    </motion.div>
                  </div>
                  <p className="text-sm font-body text-foreground">{benefit}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Quick Intake Form */}
            <motion.div 
              className="bg-muted/50 rounded-lg p-6 mb-6 shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.12)] transition-all"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              whileHover={{ backgroundColor: "rgba(var(--muted-rgb, 240, 240, 240), 0.7)", scale: 1.01 }}
              style={{ transform: 'translateZ(10px)' }}
            >
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                Snelle start (optioneel)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Input
                    type="text"
                    placeholder="Postcode (bijv. 1234AB)"
                    value={postcode}
                    onChange={(e) => setPostcode(e?.target?.value)}
                  />
                </motion.div>
                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <select
                    value={woningType}
                    onChange={(e) => setWoningType(e?.target?.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Woningtype</option>
                    <option value="vrijstaand">Vrijstaande woning</option>
                    <option value="twee-onder-een-kap">Twee-onder-een-kap</option>
                    <option value="rijtjeshuis">Rijtjeshuis</option>
                    <option value="appartement">Appartement</option>
                  </select>
                </motion.div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <Link to="/quote-request-form">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button size="xl" iconName="ArrowRight" iconPosition="right" fullWidth>
                    Start gratis advies
                  </Button>
                </motion.div>
              </Link>
              <p className="text-xs font-caption text-muted-foreground mt-3">
                Geen verplichtingen • 100% gratis • Binnen 24 uur reactie
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default CustomerConversionBlock;